import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit, computed, effect, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { TabConfig } from "@template-ui/types";
import { RoleService } from "../shared/services/roles.service";
import { PlatformAccessAdminComponent } from "./access/platform-access-admin.component";
import { PlatformAccessRolePermissionCatalogComponent } from "./access/platform-access-role-permission-catalog.component";
import { PlatformAccessRolesPermissionsComponent } from "./access/platform-access-roles-permissions.component";
import { PlatformAccessScopesComponent } from "./access/platform-access-scopes.component";
import { PlatformChangeSetWorkspaceComponent } from "./change-sets/platform-change-set-workspace.component";
import { PlatformConfigurationDiagnosticsComponent } from "./configuration-diagnostics/platform-configuration-diagnostics.component";
import { PlatformDataResetAdminComponent } from "./data-reset/platform-data-reset-admin.component";
import { EnvironmentSetupComponent } from "./environment-setup/environment-setup.component";
import { PlatformFeatureFlagAdminComponent } from "./feature-flags/platform-feature-flag-admin.component";
import { PlatformLookupMetadataAdminComponent } from "./lookup-metadata/platform-lookup-metadata-admin.component";
import { PlatformProgramPackWorkspaceComponent } from "./program-packs/platform-program-pack-workspace.component";
import { PlatformTerminologyAdminComponent } from "./terminology/platform-terminology-admin.component";
import { PlatformWorkflowRegistryDiagnosticsComponent } from "./workflow-registry-diagnostics/platform-workflow-registry-diagnostics.component";
import { WorkflowDraftDirtyStateService } from "./workflow-registry-diagnostics/workflow-draft-dirty-state.service";
import { BannerService } from "../shared/services/banner.service";
import { PlatformBusinessCalendarsComponent } from "./business-calendars/platform-business-calendars.component";
import { BusinessCalendarDraftStateService } from "./business-calendars/business-calendar-draft-state.service";
import { PlatformOperationsComponent } from "./operations/platform-operations.component";
import { PlatformOperationsStateService } from "./operations/platform-operations-state.service";
import { RecurringScheduleStateService } from "./operations/recurring-schedules/recurring-schedule-state.service";

import { PlatformReasonCatalogsComponent } from './reason-catalogs/platform-reason-catalogs.component';
import { ReasonCatalogDraftStateService } from './reason-catalogs/reason-catalog-draft-state.service';
import { PlatformAlertTemplatesComponent } from './alert-templates/platform-alert-templates.component';
import { AlertTemplateDraftStateService } from './alert-templates/alert-template-draft-state.service';

import { PlatformNoticeFidelityComponent } from './notice-fidelity/platform-notice-fidelity.component';
import { NoticeFidelityStateService } from './notice-fidelity/notice-fidelity-state.service';
import { DocumentAssetsStateService } from './document-assets/document-assets-state.service';
import { PlatformDocumentAssetsComponent } from './document-assets/platform-document-assets.component';
import { TerminologyEditorStateService } from './terminology/terminology-editor-state.service';

const WORKFLOW_DRAFT_BANNER_OWNER = "platform-admin-workflow-draft-navigation";

@Component({
    selector: "phms-platform-admin-tabs",
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./platform-admin-tabs.component.html",
    styleUrl: "./platform-admin-tabs.component.scss"
})
export class PlatformAdminTabsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly workflowDraftDirtyState = inject(WorkflowDraftDirtyStateService);
  private readonly calendarDraftState = inject(BusinessCalendarDraftStateService);
  private readonly operationsState = inject(PlatformOperationsStateService);
  private readonly scheduleState = inject(RecurringScheduleStateService);
  private readonly reasonState = inject(ReasonCatalogDraftStateService);
  private readonly alertTemplateState = inject(AlertTemplateDraftStateService);
  private readonly noticeFidelityState = inject(NoticeFidelityStateService);
  private readonly documentAssetsState = inject(DocumentAssetsStateService);
  private readonly terminologyState = inject(TerminologyEditorStateService);
  readonly isCalendarDraftNavigationBlocked = this.calendarDraftState.isNavigationBlocked;
  readonly isCalendarOrOperationsNavigationBlocked = computed(() =>
    this.isCalendarDraftNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked());
  readonly isAdminDraftNavigationBlocked = computed(() =>
    this.isWorkflowDraftNavigationBlocked() || this.isCalendarOrOperationsNavigationBlocked());
  readonly isWorkflowOrOperationsNavigationBlocked = computed(() =>
    this.isWorkflowDraftNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked());
  readonly isEditorNavigationBlocked = computed(() =>
    this.isWorkflowDraftNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked());
  private readonly bannerService = inject(BannerService);
  private readonly roles = inject(RoleService);
  private readonly canResetDemoData = this.roles.hasAnyPermissionSignal("platform_access_admin.data_reset");
  private readonly canSeedEnvironment = this.roles.hasAnyPermissionSignal("platform_access_admin.seed");
  private readonly canReadPlatformAdmin = this.roles.hasAnyPermissionSignal("platform_access_admin.read");
  private readonly canReadAlertTemplates = this.roles.hasAnyPermissionSignal('content_template.read');
  readonly hasWorkflowDraftOperationInProgress =
    this.workflowDraftDirtyState.isOperationInProgress;
  readonly isWorkflowDraftNavigationBlocked =
    this.workflowDraftDirtyState.isNavigationBlocked;
  private readonly workflowDraftBannerEffect = effect(() => {
    if (!this.hasWorkflowDraftOperationInProgress()) {
      this.bannerService.clearOwner(WORKFLOW_DRAFT_BANNER_OWNER);
      return;
    }

    this.bannerService.warning(
      this.hasWorkflowDraftOperationInProgress()
        ? "Wait for the workflow draft operation to finish before switching Platform Admin sections."
        : "Save or cancel the open workflow draft before switching Platform Admin sections.",
      {
        id: WORKFLOW_DRAFT_BANNER_OWNER,
        title: "Workflow draft open",
        dismissible: false,
        lifetime: "application",
        owner: WORKFLOW_DRAFT_BANNER_OWNER,
      },
    );
  });

  readonly tabs: TabConfig[] = [
    {
      label: "Navigation",
      fragment: "access-navigation",
      component: PlatformAccessAdminComponent,
      hidden: this.route.snapshot.data["adminTabFragment"] !== "access-navigation",
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Roles & Permissions",
      fragment: "roles-permissions",
      component: PlatformAccessRolePermissionCatalogComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Access Management",
      fragment: "user-access",
      component: PlatformAccessRolesPermissionsComponent,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Scopes",
      fragment: "scopes",
      component: PlatformAccessScopesComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Feature Flags",
      fragment: "feature-flags",
      component: PlatformFeatureFlagAdminComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Lookup & Metadata",
      fragment: "lookup-metadata",
      component: PlatformLookupMetadataAdminComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Reason catalogs",
      fragment: "reason-catalogs",
      component: PlatformReasonCatalogsComponent,
      hidden: computed(() => !this.canReadPlatformAdmin()),
      disabled: computed(() => this.workflowDraftDirtyState.isNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked()),
    },
    {
      label: 'Alert templates',
      fragment: 'alert-templates',
      component: PlatformAlertTemplatesComponent,
      hidden: computed(() => !this.canReadAlertTemplates()),
      disabled: computed(() => this.workflowDraftDirtyState.isNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked()),
    },
    {
      label: 'Notice fidelity',
      fragment: 'notice-fidelity',
      component: PlatformNoticeFidelityComponent,
      hidden: computed(() => !this.canReadAlertTemplates()),
      disabled: computed(() => this.workflowDraftDirtyState.isNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked()),
    },
    {
      label: 'Document assets',
      fragment: 'document-assets',
      component: PlatformDocumentAssetsComponent,
      hidden: computed(() => !this.canReadAlertTemplates()),
      disabled: computed(() => this.workflowDraftDirtyState.isNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.terminologyState.isNavigationBlocked()),
    },
    {
      label: "Program Packs",
      fragment: "program-packs",
      component: PlatformProgramPackWorkspaceComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Change Sets",
      fragment: "change-sets",
      component: PlatformChangeSetWorkspaceComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Terminology",
      fragment: "terminology",
      component: PlatformTerminologyAdminComponent,
      disabled: computed(() => this.workflowDraftDirtyState.isNavigationBlocked() || this.calendarDraftState.isNavigationBlocked() || this.operationsState.isOperationInProgress() || this.scheduleState.isNavigationBlocked() || this.reasonState.isNavigationBlocked() || this.alertTemplateState.isNavigationBlocked() || this.noticeFidelityState.isNavigationBlocked() || this.documentAssetsState.isNavigationBlocked()),
    },
    {
      label: "Configuration Diagnostics",
      fragment: "configuration-diagnostics",
      component: PlatformConfigurationDiagnosticsComponent,
      hidden: true,
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Workflow Registry",
      fragment: "workflow-registry-diagnostics",
      component: PlatformWorkflowRegistryDiagnosticsComponent,
      disabled: this.isCalendarOrOperationsNavigationBlocked,
    },
    {
      label: "Data Reset",
      fragment: "data-reset",
      component: PlatformDataResetAdminComponent,
      hidden: computed(() => !this.canResetDemoData()),
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Environment setup",
      fragment: "environment-setup",
      component: EnvironmentSetupComponent,
      hidden: computed(() => !this.canSeedEnvironment()),
      disabled: this.isAdminDraftNavigationBlocked,
    },
    {
      label: "Business calendars",
      fragment: "business-calendars",
      component: PlatformBusinessCalendarsComponent,
      hidden: computed(() => !this.canReadPlatformAdmin()),
      disabled: this.isWorkflowOrOperationsNavigationBlocked,
    },
    {
      label: "Jobs & outbox",
      fragment: "operations",
      component: PlatformOperationsComponent,
      hidden: computed(() => !this.canReadPlatformAdmin()),
      disabled: this.isEditorNavigationBlocked,
    },
  ];

  ngOnInit(): void {
    const defaultTabFragment = this.route.snapshot.data["adminTabFragment"] as string | undefined;
    const currentFragment = this.route.snapshot.fragment;
    if (!defaultTabFragment || currentFragment) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      fragment: defaultTabFragment,
      queryParamsHandling: "preserve",
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.workflowDraftBannerEffect.destroy();
    this.bannerService.clearOwner(WORKFLOW_DRAFT_BANNER_OWNER);
  }
}
